import { useCallback, useState } from 'react';
import { dealDetailSchema, type DealDetail } from '@forecastify/shared';
import { useT } from '../../i18n';
import { useLiveInvalidation } from '../../live/use-live';
import { DEAL_TOPICS } from '../../live/topics';
import { useSession } from '../../session';
import { useFirstLoad } from '../use-first-load';
import { failureOf, type ScreenFailure } from '../screen-state';
import { dealRefusal } from './deal-kit';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · one deal record, and every write against it.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Every deal write answers with the whole record (`deal.service.ts` `assemble`), so `write` swaps
 * the state for the answer — the plan's totals, the feed and the stepper move together without a
 * second request. ⚠️ A refused write is NOT a screen failure: it comes back as a sentence for the
 * caller to show beside what was being changed, and the record stays as it was.
 */

export interface DealRecord {
  readonly deal: DealDetail | null;
  readonly loading: boolean;
  readonly failure: ScreenFailure | null;
  readonly reload: () => Promise<void>;
  /**
   * A write whose answer is the record. Resolves to `null` on success, or the refusal's sentence.
   * `path` is relative to the deal: `/won`, `/line-items/<id>`, or `''` for the deal itself.
   */
  readonly write: (path: string, method: string, body?: unknown) => Promise<string | null>;
  /** The same, for an endpoint that answers 204 (favourite, delete): reloads after. */
  readonly send: (path: string, method: string, body?: unknown) => Promise<string | null>;
}

export function useDealRecord(ref: string): DealRecord {
  const session = useSession();
  const t = useT();
  const [deal, setDeal] = useState<DealDetail | null>(null);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }
    const response = await session.request(`/deals/${encodeURIComponent(ref)}`).catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }
    const parsed = dealDetailSchema.safeParse(await response.json());
    if (!parsed.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setFailure(null);
    setDeal(parsed.data);
  }, [session, ref]);

  const loading = useFirstLoad(reload);
  useLiveInvalidation(DEAL_TOPICS, reload);

  const request = useCallback(
    async (path: string, method: string, body: unknown): Promise<Response | string> => {
      const id = deal?.id ?? ref;
      const response = await session
        .request(`/deals/${encodeURIComponent(id)}${path}`, {
          method,
          ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        })
        .catch(() => null);
      if (response === null) return t('error.unavailable');
      if (!response.ok) return dealRefusal(response, t);
      return response;
    },
    [session, deal, ref, t],
  );

  const write = useCallback(
    async (path: string, method: string, body?: unknown): Promise<string | null> => {
      const answer = await request(path, method, body);
      if (typeof answer === 'string') return answer;
      const parsed = dealDetailSchema.safeParse(await answer.json());
      if (parsed.success) setDeal(parsed.data);
      else await reload();
      return null;
    },
    [request, reload],
  );

  const send = useCallback(
    async (path: string, method: string, body?: unknown): Promise<string | null> => {
      const answer = await request(path, method, body);
      if (typeof answer === 'string') return answer;
      await reload();
      return null;
    },
    [request, reload],
  );

  return { deal, loading, failure, reload, write, send };
}
